import { useCallback, useState } from 'react';
import { getToken } from '../services/api';
import { useT } from '../i18n';
// Issue #550: Page shell — remove wrapper proprio (max-w-3xl p-6
// duplicava o <main> do Layout) e padroniza o header da pagina.
import Page from '../components/Page';

// Issue #475: Status de servico SEFAZ — consulta FUNCIONAL via endpoints
// proxy do auranota-api (o browser nao chama o MCP direto: X-Tenant-ID /
// X-Session-ID sao credenciais server-side). Nao requer certificado.

const UFS = [
  'AC', 'AL', 'AM', 'AP', 'BA', 'CE', 'DF', 'ES', 'GO', 'MA', 'MG', 'MS',
  'MT', 'PA', 'PB', 'PE', 'PI', 'PR', 'RJ', 'RN', 'RO', 'RR', 'RS', 'SC',
  'SE', 'SP', 'TO',
] as const;

type DocType = 'nfe' | 'cte' | 'mdfe';

const ENDPOINTS: Record<DocType, (uf: string) => string> = {
  nfe: (uf) => `/api/sefaz/status/${uf}`,
  cte: (uf) => `/api/sefaz/status-cte/${uf}`,
  mdfe: (uf) => `/api/sefaz/status-mdfe/${uf}`,
};

interface StatusPayload {
  cStat?: number | string;
  xMotivo?: string;
  uf?: string;
  dhRecbto?: string;
  ts?: string;
  [key: string]: unknown;
}

interface FetchError {
  status: number;
  message: string;
  retryAfterSec?: number;
}

async function fetchStatus(docType: DocType, uf: string): Promise<StatusPayload> {
  const token = getToken();
  const res = await fetch(ENDPOINTS[docType](uf), {
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    const detail = data?.detail;
    const err: FetchError = {
      status: res.status,
      message:
        (typeof detail === 'object' && detail?.error) ||
        (typeof detail === 'string' && detail) ||
        `HTTP ${res.status}`,
    };
    if (res.status === 429) {
      const header = res.headers.get('Retry-After');
      const fromBody =
        typeof detail === 'object' && detail?.retryAfterSec != null
          ? Number(detail.retryAfterSec)
          : undefined;
      err.retryAfterSec = fromBody ?? (header ? Number(header) : undefined);
    }
    throw err;
  }
  return data as StatusPayload;
}

function isFetchError(err: unknown): err is FetchError {
  return typeof err === 'object' && err !== null && 'status' in err;
}

function SefazStatus() {
  const t = useT();
  const [uf, setUf] = useState<string>('SP');
  const [docType, setDocType] = useState<DocType>('nfe');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<StatusPayload | null>(null);
  const [error, setError] = useState<FetchError | null>(null);

  const handleConsult = useCallback(async () => {
    setLoading(true);
    setError(null);
    setResult(null);
    try {
      setResult(await fetchStatus(docType, uf));
    } catch (err) {
      if (isFetchError(err)) {
        setError(err);
      } else {
        setError({ status: 0, message: err instanceof Error ? err.message : String(err) });
      }
    } finally {
      setLoading(false);
    }
  }, [docType, uf]);

  const timestamp = result?.dhRecbto ?? result?.ts;
  const inOperation = String(result?.cStat) === '107';

  return (
    <Page title={t('sefaz.statusPage.title')}>
      {/* Doc type tabs */}
      <div className="mb-6 flex gap-2 border-b border-brand-200">
        {(['nfe', 'cte', 'mdfe'] as DocType[]).map((dt) => (
          <button
            key={dt}
            className={`px-4 py-2 font-medium ${docType === dt ? 'border-b-2 border-brand-600 text-brand-700' : 'text-brand-400'}`}
            onClick={() => { setDocType(dt); setResult(null); setError(null); }}
          >
            {t(`sefaz.statusPage.tab.${dt}`)}
          </button>
        ))}
      </div>

      <div className="mb-4 flex items-end gap-3">
        <div>
          <label className="mb-1 block text-sm font-medium text-brand-600">
            {t('sefaz.statusPage.ufLabel')}
          </label>
          <select
            value={uf}
            onChange={(e) => setUf(e.target.value)}
            className="rounded-lg border border-brand-200 bg-white px-3 py-2 text-sm"
          >
            {UFS.map((u) => (
              <option key={u} value={u}>{u}</option>
            ))}
          </select>
        </div>
        <button
          onClick={handleConsult}
          disabled={loading}
          className="rounded-lg bg-brand-600 px-6 py-2 font-medium text-white disabled:cursor-not-allowed disabled:opacity-50"
        >
          {loading ? t('common.processing') : t('sefaz.statusPage.consult')}
        </button>
      </div>

      {error && (
        <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-700">
          {error.status === 429
            ? t('sefaz.statusPage.rateLimited', { s: error.retryAfterSec ?? '?' })
            : t('sefaz.statusPage.unavailable')}
          <span className="mt-1 block text-xs text-red-500">{error.message}</span>
        </div>
      )}

      {result && (
        <div
          className={`rounded-lg border p-4 ${
            inOperation
              ? 'border-green-200 bg-green-50'
              : 'border-amber-200 bg-amber-50'
          }`}
        >
          <dl className="space-y-1 text-sm">
            <div>
              <dt className="inline font-medium">{t('sefaz.statusPage.cstat')}: </dt>
              <dd className={`inline font-semibold ${inOperation ? 'text-green-700' : 'text-amber-700'}`}>
                {result.cStat ?? '—'}
              </dd>
            </div>
            <div>
              <dt className="inline font-medium">{t('sefaz.statusPage.xmotivo')}: </dt>
              <dd className="inline">{result.xMotivo ?? '—'}</dd>
            </div>
            <div>
              <dt className="inline font-medium">{t('sefaz.statusPage.uf')}: </dt>
              <dd className="inline">{result.uf ?? uf}</dd>
            </div>
            <div>
              <dt className="inline font-medium">{t('sefaz.statusPage.timestamp')}: </dt>
              <dd className="inline">{timestamp ?? '—'}</dd>
            </div>
          </dl>
        </div>
      )}
    </Page>
  );
}

export default SefazStatus;
